import {
  ROSTER_FILTER_KIND,
  type PayrollRosterField,
  type RosterFieldFilter,
} from "@/payroll/roster/types";
import { formatRosterValue } from "@/payroll/roster/utils/changes";
import { parseRosterRangeBound, ROSTER_NO_VALUE_LABEL } from "./filter-state";

const MAX_NAMED_FILTER_VALUES = 2;

const describeRosterFilter = (
  field: PayrollRosterField,
  filter: RosterFieldFilter,
  full: boolean,
): string => {
  const conditions: string[] = [];
  const formatBound = (bound: number) =>
    formatRosterValue(String(bound), field);

  switch (filter.kind) {
    case ROSTER_FILTER_KIND.options:
      if (
        filter.values.length > 0 &&
        (full || filter.values.length <= MAX_NAMED_FILTER_VALUES)
      ) {
        conditions.push(filter.values.join(" or "));
      } else if (filter.values.length > MAX_NAMED_FILTER_VALUES) {
        conditions.push(`any of ${filter.values.length}`);
      }
      break;
    case ROSTER_FILTER_KIND.boolean:
      if (filter.values.length === 1) {
        conditions.push(filter.values[0] ? "Yes" : "No");
      } else if (filter.values.length > 1) {
        conditions.push("Yes or No");
      }
      break;
    case ROSTER_FILTER_KIND.range: {
      const min = parseRosterRangeBound(filter.min);
      const max = parseRosterRangeBound(filter.max);
      if (min !== null && max !== null) {
        conditions.push(
          min === max
            ? formatBound(min)
            : `${formatBound(min)}–${formatBound(max)}`,
        );
      } else if (min !== null) {
        conditions.push(`${formatBound(min)} or more`);
      } else if (max !== null) {
        conditions.push(`${formatBound(max)} or less`);
      }
      break;
    }
    case ROSTER_FILTER_KIND.text:
      if (filter.query.trim()) {
        return `contains “${filter.query.trim()}”${
          filter.includeBlank
            ? ` or is ${ROSTER_NO_VALUE_LABEL.toLocaleLowerCase()}`
            : ""
        }`;
      }
      break;
  }

  if (filter.includeBlank) {
    conditions.push(ROSTER_NO_VALUE_LABEL.toLocaleLowerCase());
  }

  return `is ${conditions.join(" or ")}`;
};

export const rosterFilterSummary = (
  field: PayrollRosterField,
  filter: RosterFieldFilter,
): string => describeRosterFilter(field, filter, false);

export const rosterFilterDetail = (
  field: PayrollRosterField,
  filter: RosterFieldFilter,
): string => `${field.label} ${describeRosterFilter(field, filter, true)}`;
